import * as Select from "@radix-ui/react-select"

type FormSelectOption = {
  value: string
  label: string
}

type FormSelectProps = {
  id?: string
  name?: string
  value: string
  onValueChange: (value: string) => void
  options: FormSelectOption[] | string[]
  required?: boolean
}

const normalizeOptions = (options: FormSelectOption[] | string[]): FormSelectOption[] =>
  options.map((o: FormSelectOption | string) => (typeof o === "string" ? { value: o, label: o } : o))

export const FormSelect = ({ id, name, value, onValueChange, options, required }: FormSelectProps) => {
  const normalized = normalizeOptions(options)
  const selectedLabel = normalized.find((opt) => opt.value === value)?.label ?? value

  return (
    <>
      <Select.Root
        value={value}
        onValueChange={onValueChange}
        required={required}
        name={name}
      >
        <Select.Trigger
          data-id={id}
          id={id}
          className="group box-border flex h-[40px] max-h-[40px] min-h-[40px] w-full cursor-pointer appearance-none items-center justify-between gap-2 overflow-hidden rounded-none border border-[#caced1] bg-white px-4 py-2 text-left font-[inherit] text-[16px] text-black outline-none focus-visible:outline-2 focus-visible:outline-[var(--pink)] focus-visible:outline-offset-0"
          aria-label={name}
        >
          <Select.Value>{selectedLabel}</Select.Value>
          <Select.Icon className="mr-2 flex shrink-0 items-center">
            <span className="block h-0 w-0 border-t-[0.5rem] border-t-black border-r-[0.5rem] border-r-transparent border-l-[0.5rem] border-l-transparent shadow-[0_-2px_0_#000] transition-transform duration-200 ease-[ease] group-data-[state=open]:rotate-180" />
          </Select.Icon>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            className="z-[200] max-h-[var(--radix-select-content-available-height)] w-[var(--radix-select-trigger-width)] overflow-hidden border-2 border-[var(--deep-blue)] bg-[var(--white)]"
            position="popper"
            sideOffset={0}
            avoidCollisions
          >
            <Select.Viewport className="py-1">
              {normalized.map((opt) => (
                <Select.Item
                  key={opt.value}
                  value={opt.value}
                  className="flex cursor-pointer select-none items-center justify-between gap-2 px-4 py-2 font-[inherit] text-[16px] text-[var(--deep-blue)] outline-none data-[highlighted]:bg-[var(--deep-blue)] data-[state=checked]:font-bold data-[highlighted]:text-[var(--white)]"
                >
                  <Select.ItemText>{opt.label}</Select.ItemText>
                  <Select.ItemIndicator className="text-[0.8em] opacity-80">
                    <i className="fa-sharp fa-solid fa-check" />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </>
  )
}
